import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient';
import type { SiteEmailLog, SiteEmailLogDetails, SiteEmailPagedResponse, SiteEmailSearchParams } from '@/types/siteEmail';

function cleanParams(params: SiteEmailSearchParams = {}) {
	const cleaned: Record<string, unknown> = {};

	Object.entries(params).forEach(([key, value]) => {
		if (value !== null && value !== undefined && value !== '') {
			cleaned[key] = value;
		}
	});

	return cleaned;
}

export const useSiteEmailStore = defineStore('siteEmailStore', {
	state: () => ({
		emailLogs: [] as SiteEmailLog[],
		selectedEmailLog: null as SiteEmailLogDetails | null,

		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 25,

		loading: false,
		detailsLoading: false,
		error: null as string | null
	}),

	actions: {
		async fetchEmailLogs(params: SiteEmailSearchParams = {}) {
			this.loading = true;
			this.error = null;

			try {
				const { data } = await apiClient.get<SiteEmailPagedResponse>('/emails/all', {
					params: cleanParams({
						page: 0,
						size: 25,
						sort: 'createdAt,desc',
						...params
					})
				});

				this.emailLogs = Array.isArray(data.content) ? data.content : [];
				this.totalElements = data.totalElements;
				this.totalPages = data.totalPages;
				this.page = data.number;
				this.size = data.size;

				return data;
			} catch (error) {
				this.emailLogs = [];
				this.totalElements = 0;
				this.totalPages = 0;
				this.error = 'Unable to load email audit logs.';
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async fetchEmailLogDetails(siteEmailLogNumber: number) {
			this.detailsLoading = true;
			this.error = null;

			try {
				const { data } = await apiClient.get<SiteEmailLogDetails>(`/emails/${siteEmailLogNumber}`);

				this.selectedEmailLog = data;
				return data;
			} catch (error) {
				this.selectedEmailLog = null;
				this.error = 'Unable to load email audit details.';
				throw error;
			} finally {
				this.detailsLoading = false;
			}
		},

		async fetchEmailLogsForEntity(relatedEntityType: string, relatedEntityNumber: number, params: Omit<SiteEmailSearchParams, 'relatedEntityType' | 'relatedEntityNumber'> = {}) {
			return this.fetchEmailLogs({
				...params,
				relatedEntityType,
				relatedEntityNumber
			});
		},

		clearSelectedEmailLog() {
			this.selectedEmailLog = null;
		}
	}
});
